import { Box, Button, CircularProgress, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridRow } from '@mui/x-data-grid'
import NoRecords from 'components/NoRecords'
import { useBasePath } from 'components/Providers'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { saveSelf as approvalPatch, removeItem } from 'store/leave/punch-request'
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import dayjs from 'dayjs'
import * as snackbar from '../../../../utils/snackbar'
import { capitalize } from 'lodash'

const TooltipRow = (props) => {
  const title = props.row?.createdAt
    ? `Requested: ${dayjs(props.row.createdAt).format('MMM D, YYYY h:mm A')}`
    : ''
  return (
    <Tooltip title={title} arrow placement="top" followCursor disableInteractive>
      <GridRow {...props} />
    </Tooltip>
  )
}

function PunchApprovalIndex() {
  const punchApprovals = useSelector(state => state.leave.punchRequest.list)
  const loading = useSelector(state => state.leave.punchRequest.loading)
  const punchRequests = punchApprovals?.filter(p => p?.type !== 'permission') ?? []
  const permissionRequests = punchApprovals?.filter(p => p?.type === 'permission') ?? []
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const basePath = useBasePath()
  const navigate = useNavigate()

  const handleApprove = async id => {
    try {
      await dispatch(approvalPatch({ id, status: 'approved' })).unwrap()
      snackbar.success('Successfully Approved')
      dispatch(removeItem(id))
    } catch {
      snackbar.error('Error Saving! Please try again later')
    }
  }

  const columns = [
    {
      field: 'lastName',
      headerName: t('Last Name'),
      flex: 1,
      sortable: true,
      renderCell: params => params.row?.requester?.lastName || '—',
    },
    {
      field: 'firstName',
      headerName: t('First Name'),
      flex: 1,
      sortable: true,
      renderCell: params => params.row?.requester?.firstName || '—',
    },
    {
      field: 'date',
      headerName: t('Date'),
      flex: 1,
      sortable: true,
      renderCell: params => dayjs(params.row.date).format('MM/DD/YYYY'),
    },
    {
      field: 'type',
      headerName: t('Request Type'),
      flex: 1,
      sortable: true,
      renderCell: params => capitalize(params.row.type) || '—',
    },
    {
      field: 'note',
      headerName: t('Notes'),
      flex: 2,
      sortable: false,
      renderCell: params => params.row.note || '—',
    },
    {
      field: 'action',
      headerName: t('Action'),
      flex: 1,
      sortable: false,
      renderCell: params => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => handleApprove(params.row.id)}
          >
            {t('Approve')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={() => navigate(`${basePath}/${params.row.id}`, { state: { record: params.row } })}
          >
            {t('Deny')}
          </Button>
        </Stack>
      ),
    },
  ]

  const noRowsOverlay = () => (
    <Box display="flex" alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
      {loading ? (
        <CircularProgress size={36} />
      ) : (
        <NoRecords message={t('No records found')} />
      )}
    </Box>
  )

  const timeColumn = {
    field: 'time',
    headerName: t('Time'),
    flex: 1,
    sortable: false,
    renderCell: params => {
      if (!params.row.time) return '—'
      const parsed = dayjs(`2000-01-01T${params.row.time}`)
      return parsed.isValid() ? parsed.format('h:mm A') : params.row.time
    },
  }

  const punchColumns = [
    ...columns.slice(0, 3),
    timeColumn,
    ...columns.slice(3),
  ]

  const renderSection = (title, icon, rows, cols) => (
    <Paper sx={{ mb: 3, width: '100%', overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        {icon}
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        <Typography variant="body2" color="text.disabled">({rows.length})</Typography>
      </Stack>
      <DataGrid
        rows={rows}
        columns={cols}
        getRowId={row => row.id}
        autoHeight
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        sx={{ border: 'none' }}
        slots={{ noRowsOverlay, row: TooltipRow }}
      />
    </Paper>
  )

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      {renderSection(
        t('Punch Requests'),
        <AssignmentLateIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
        punchRequests,
        punchColumns,
      )}
      {renderSection(
        t('Permission Requests'),
        <EventAvailableIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
        permissionRequests,
        columns,
      )}
    </Box>
  )
}

export default PunchApprovalIndex
